import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import client from '@/api/client';
import type { Hostel, RentStatusEntry, Complaint, PageResponse, DashboardStats } from '@/api/types';
import type { HostelStatsEntry } from '@/features/dashboard/types';

const STATS_DEFAULTS: DashboardStats = {
  totalRooms: 0, totalBeds: 0, occupiedBeds: 0, reservedBeds: 0, vacantBeds: 0, blockedBeds: 0,
  occupancyRate: 0, activeTenants: 0, checkedOutTenants: 0, cancelledTenants: 0,
  overdueRent: 0, collectedThisMonth: 0, pendingAmount: 0, overdueAmount: 0, collectionRate: 0,
  monthlyRevenue: [], criticalComplaints: 0, complaintsPending3Days: 0, resolvedThisWeek: 0,
  avgResolutionTime: 0, occupancyTrend: [],
  expectedCollectionToday: 0, monthlyTarget: 0, checkingInToday: 0, checkingOutTomorrow: 0, stayEndingSoon: 0,
};

function sumStats(entries: DashboardStats[]): DashboardStats {
  return entries.reduce<DashboardStats>((acc, r) => ({
    totalRooms: acc.totalRooms + r.totalRooms,
    totalBeds: acc.totalBeds + r.totalBeds,
    occupiedBeds: acc.occupiedBeds + r.occupiedBeds,
    reservedBeds: acc.reservedBeds + r.reservedBeds,
    vacantBeds: acc.vacantBeds + r.vacantBeds,
    blockedBeds: acc.blockedBeds + r.blockedBeds,
    occupancyRate: 0,
    activeTenants: acc.activeTenants + r.activeTenants,
    checkedOutTenants: acc.checkedOutTenants + r.checkedOutTenants,
    cancelledTenants: acc.cancelledTenants + r.cancelledTenants,
    overdueRent: acc.overdueRent + r.overdueRent,
    collectedThisMonth: acc.collectedThisMonth + Number(r.collectedThisMonth),
    pendingAmount: acc.pendingAmount + Number(r.pendingAmount),
    overdueAmount: acc.overdueAmount + Number(r.overdueAmount),
    collectionRate: 0,
    monthlyRevenue: acc.monthlyRevenue,
    criticalComplaints: acc.criticalComplaints + r.criticalComplaints,
    complaintsPending3Days: acc.complaintsPending3Days + r.complaintsPending3Days,
    resolvedThisWeek: acc.resolvedThisWeek + r.resolvedThisWeek,
    avgResolutionTime: 0,
    occupancyTrend: acc.occupancyTrend,
    expectedCollectionToday: acc.expectedCollectionToday + Number(r.expectedCollectionToday),
    monthlyTarget: acc.monthlyTarget + Number(r.monthlyTarget),
    checkingInToday: acc.checkingInToday + r.checkingInToday,
    checkingOutTomorrow: acc.checkingOutTomorrow + r.checkingOutTomorrow,
    stayEndingSoon: acc.stayEndingSoon + r.stayEndingSoon,
  }), { ...STATS_DEFAULTS });
}

export function useSupervisorDashboardData() {
  const { data: hostels = [], isLoading: hostelsLoading } = useQuery<Hostel[]>({
    queryKey: ['supervisor-hostels'],
    queryFn: () => client.get('/api/hostels/my').then(r => r as unknown as Hostel[]),
  });

  const activeHostels = hostels.filter(h => h.status === 'ACTIVE');
  const multiHostel = activeHostels.length > 1;

  const [selectedId, setSelectedId] = useState<string>('ALL');
  const isAll = multiHostel && selectedId === 'ALL';
  const activeHostelId = multiHostel ? (isAll ? '' : selectedId) : (activeHostels[0]?.id ?? '');

  // Per-hostel stats for every assigned hostel — powers both the Property Overview table
  // and the aggregated "ALL" view, so we only fetch each hostel's stats once.
  const { data: perHostelStats = [], isLoading: statsLoading } = useQuery<HostelStatsEntry[]>({
    queryKey: ['supervisor-dashboard-per-hostel', activeHostels.map(h => h.id).join(',')],
    queryFn: async () => {
      const results = await Promise.all(
        activeHostels.map(async h => ({
          hostel: h,
          stats: await client.get(`/api/dashboard/supervisor/${h.id}`) as DashboardStats,
        }))
      );
      return results;
    },
    enabled: activeHostels.length > 0,
  });

  const allStats = useMemo(() => sumStats(perHostelStats.map(p => p.stats)), [perHostelStats]);

  const s: DashboardStats = isAll
    ? allStats
    : (perHostelStats.find(p => p.hostel.id === activeHostelId)?.stats ?? STATS_DEFAULTS);

  const { data: rentEntries = [] } = useQuery<RentStatusEntry[]>({
    queryKey: ['supervisor-rent-status', activeHostelId],
    queryFn: () => client.get(`/api/rent/status?hostelId=${activeHostelId}`).then(r => r as unknown as RentStatusEntry[]),
    enabled: !!activeHostelId,
  });

  const { data: allRentEntries = [] } = useQuery<RentStatusEntry[]>({
    queryKey: ['supervisor-rent-status-all', activeHostels.map(h => h.id).join(',')],
    queryFn: async () => {
      const results = await Promise.all(
        activeHostels.map(h => client.get(`/api/rent/status?hostelId=${h.id}`) as Promise<RentStatusEntry[]>)
      );
      return results.flat();
    },
    enabled: isAll,
  });

  const rentList = isAll ? allRentEntries : rentEntries;
  const billableRent = rentList.filter(e => e.status !== 'WAIVED');
  const totalToCollect = billableRent.reduce((sum, e) => sum + Number(e.amountDue), 0);
  const paidAmount = billableRent.reduce((sum, e) => sum + Number(e.amountPaid), 0);
  const remaining = totalToCollect - paidAmount;
  const paidEntries = billableRent.filter(e => Number(e.amountPaid) > 0);
  const unpaidEntries = billableRent.filter(e => Number(e.amountDue) - Number(e.amountPaid) > 0);

  const { data: complaintPage } = useQuery<PageResponse<Complaint>>({
    queryKey: ['sup-complaints', activeHostelId],
    queryFn: () => client.get(`/api/complaints?hostelId=${activeHostelId}&size=200`).then(r => r as unknown as PageResponse<Complaint>),
    enabled: !!activeHostelId,
  });
  const { data: allComplaintList = [] } = useQuery<Complaint[]>({
    queryKey: ['sup-complaints-all', activeHostels.map(h => h.id).join(',')],
    queryFn: async () => {
      const pages = await Promise.all(
        activeHostels.map(h => client.get(`/api/complaints?hostelId=${h.id}&size=200`).then(r => (r as unknown as PageResponse<Complaint>).content))
      );
      return pages.flat();
    },
    enabled: isAll,
  });
  const complaintList = isAll ? allComplaintList : (complaintPage?.content ?? []);
  const openComplaints       = complaintList.filter(c => c.status === 'OPEN');
  const inProgressComplaints = complaintList.filter(c => c.status === 'IN_PROGRESS');
  const resolvedComplaints   = complaintList.filter(c => c.status === 'RESOLVED');

  const revenueData = (s.monthlyRevenue ?? []).map(([month, amount]: [string, number]) => ({
    month,
    amount: Number(amount),
  }));

  // Combined so the dashboard's loading gate covers both the hostel list AND its per-hostel
  // stats — otherwise the spinner disappears the instant the (fast) hostel list resolves,
  // while the stat cards still briefly render on STATS_DEFAULTS (all zeros) until the
  // slower per-hostel stats call finishes.
  const isLoading = hostelsLoading || statsLoading;

  return {
    hostels, isLoading,
    activeHostels, multiHostel, selectedId, setSelectedId, isAll, activeHostelId,
    s, perHostelStats,
    rentList, billableRent, totalToCollect, paidAmount, remaining, paidEntries, unpaidEntries,
    complaintList, openComplaints, inProgressComplaints, resolvedComplaints,
    revenueData,
  };
}
